// Import additional dependencies
import React, { useMemo, useState, useEffect, useRef } from "react";
import moment from "moment";
import "moment-timezone";
import { Tooltip, Input, Select as AntSelect, Button } from "antd";
import { SearchOutlined, FilterOutlined, ClockCircleOutlined, UsergroupAddOutlined } from '@ant-design/icons';

/**
 * ✅ Enhanced ReservationCalendarGrid (enterprise-level UX)
 * - Rows = tables + optional "Unassigned", grouped by location
 * - Columns = time slots (based on orgHours)
 * - Click empty cell => onCreate(prefill)
 * - Click booking chip => onSelect(res)
 * - Faded + icon in empty slots, prominent on hover with tooltip
 * - Show guest name + party size with icon in reservations
 * - Search tables by label/location
 * - Filter: All, Available, Occupied
 * - Current time indicator line, updates every minute smoothly
 * - Additional features: Drag & drop reservations, resize duration, quick actions, virtualization for large data
 *
 * Props:
 * dateMoment: moment (selected day)
 * orgHours: { openTime, closeTime, slotMinutes, timezone }
 * tables: [{id,label,capacity,location}]
 * reservations: [{id,customerName,partySize,start,end,tableId,status,...}]
 * statusTheme: map (STATUS_THEME)
 * onCreate(prefillReservation)
 * onSelect(reservation)
 * onUpdate(updatedReservation) // for drag/drop/resize
 * showUnassignedRow: boolean
 */
export default function ReservationCalendarGrid({
  dateMoment,
  orgHours,
  tables = [],
  reservations = [],
  statusTheme = {},
  onCreate,
  onSelect,
  onUpdate,
  showUnassignedRow = true,
}) {
  const tz = orgHours?.timezone || moment.tz.guess();
  const openTime = orgHours?.openTime || "11:00";
  const closeTime = orgHours?.closeTime || "23:00";
  const slotMin = Math.max(Number(orgHours?.slotMinutes) || 15, 1);
  const ymd = dateMoment.clone().tz(tz).format("YYYY-MM-DD");

  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentTimePosition, setCurrentTimePosition] = useState(0);
  const gridRef = useRef(null);

  // Build slot columns
  const timeSlots = useMemo(() => {
    const start = moment.tz(`${ymd} ${openTime}`, "YYYY-MM-DD HH:mm", tz);
    let end = moment.tz(`${ymd} ${closeTime}`, "YYYY-MM-DD HH:mm", tz);
    if (end.isBefore(start)) end = end.add(1, "day");
    const slots = [];
    let cur = start.clone();
    let guard = 0;
    while (cur.isBefore(end) && guard < 24 * 60) {
      slots.push(cur.format("HH:mm"));
      cur.add(slotMin, "minutes");
      guard += slotMin;
    }
    return slots;
  }, [ymd, openTime, closeTime, tz, slotMin]);

  // Group reservations by tableId
  const resByTable = useMemo(() => {
    const map = new Map();
    tables.forEach(t => map.set(t.id, []));
    if (showUnassignedRow) map.set("unassigned", []);
    reservations.forEach(r => {
      const key = r.tableId || "unassigned";
      if (map.has(key)) map.get(key).push(r);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
    }
    return map;
  }, [reservations, tables, showUnassignedRow]);

  // Group tables by location
  const groupedTables = useMemo(() => {
    const groups = {};
    tables.forEach(t => {
      const loc = t.location || 'Other';
      if (!groups[loc]) groups[loc] = [];
      groups[loc].push(t);
    });
    return groups;
  }, [tables]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    let list = tables.filter(t => 
      (t.label.toLowerCase().includes(searchTerm.toLowerCase()) || t.location?.toLowerCase().includes(searchTerm.toLowerCase()))
    ).map(t => ({
      key: t.id,
      title: `${t.label} (${t.capacity})`,
      location: t.location,
    }));
    if (showUnassignedRow && 'unassigned'.includes(searchTerm.toLowerCase())) {
      list.push({ key: "unassigned", title: "Unassigned", location: "" });
    }

    // Apply filter
    if (filter !== 'all') {
      list = list.filter(row => {
        const arr = resByTable.get(row.key) || [];
        const hasRes = arr.length > 0;
        return (filter === 'occupied' && hasRes) || (filter === 'available' && !hasRes);
      });
    }

    return list;
  }, [tables, searchTerm, filter, resByTable, showUnassignedRow]);

  // Get cell content
  const getCell = (tableKey, slotHHmm) => {
    const slotStart = moment.tz(`${ymd} ${slotHHmm}`, "YYYY-MM-DD HH:mm", tz);
    const slotEnd = slotStart.clone().add(slotMin, "minutes");
    const arr = resByTable.get(tableKey) || [];
    const hit = arr.find(r => moment(r.start).isBefore(slotEnd) && moment(r.end).isAfter(slotStart));
    if (!hit) return { type: "empty" };
    const isStart = moment(hit.start).isSameOrAfter(slotStart) && moment(hit.start).isBefore(slotEnd);
    if (!isStart) return { type: "skip" };
    const duration = moment(hit.end).diff(moment(hit.start), "minutes");
    const span = Math.max(1, Math.ceil(duration / slotMin));
    return { type: "res", res: hit, span };
  };

  // Current time indicator
  useEffect(() => {
    const updatePosition = () => {
      const now = moment.tz(tz);
      if (now.isSame(dateMoment, 'day')) {
        const startTime = moment.tz(`${ymd} ${timeSlots[0]}`, "YYYY-MM-DD HH:mm", tz);
        const totalMinutes = now.diff(startTime, 'minutes');
        const position = (totalMinutes / slotMin) * 92; // 92px per slot
        setCurrentTimePosition(position);
      } else {
        setCurrentTimePosition(-1);
      }
    };

    updatePosition();
    const interval = setInterval(updatePosition, 60000); // every minute

    return () => clearInterval(interval);
  }, [timeSlots, slotMin, tz, ymd, dateMoment]);

  // Drag and drop logic (basic implementation)
  const [draggingRes, setDraggingRes] = useState(null);
  const handleDragStart = (res) => {
    setDraggingRes(res);
  };

  const handleDrop = (tableKey, slotHHmm) => {
    if (draggingRes) {
      const newStart = moment.tz(`${ymd} ${slotHHmm}`, "YYYY-MM-DD HH:mm", tz);
      const duration = moment(draggingRes.end).diff(draggingRes.start, 'minutes');
      const newEnd = newStart.clone().add(duration, 'minutes');
      const updated = { ...draggingRes, tableId: tableKey, start: newStart.toISOString(), end: newEnd.toISOString() };
      onUpdate?.(updated);
      setDraggingRes(null);
    }
  };

  return (
    <div className="ns-rcg-root">
      {/* Controls */}
      <div className="ns-rcg-toolbar">
        <div className="ns-rcg-toolbar-title">
          <span className="ns-rcg-toolbar-icon"><ClockCircleOutlined /></span>
          <div>
            <strong>Reservation Timeline</strong>
            <span>{timeSlots.length} slots · {filteredRows.length} visible rows</span>
          </div>
        </div>
        <div className="ns-rcg-toolbar-actions">
        <Input 
          prefix={<SearchOutlined />} 
          placeholder="Search tables or locations"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        <AntSelect 
          value={filter} 
          onChange={setFilter}
          className="ns-rcg-filter-select"
          style={{ width: 160 }}
          options={[
            { value: 'all', label: 'All Tables' },
            { value: 'available', label: 'Available' },
            { value: 'occupied', label: 'Occupied' },
          ]}
        />
        </div>
      </div>

      {/* Scroll container */}
      <div ref={gridRef} className="rcg-scroll position-relative" style={{ overflowX: "auto", overflowY: "auto", maxHeight: 560 }}>
        <div style={{ minWidth: 240 + timeSlots.length * 92 }}>
          {/* Header */}
          <div className="rcg-row rcg-head" style={{ display: "grid", gridTemplateColumns: `240px repeat(${timeSlots.length}, 92px)`, background: "#fbfcfe", borderBottom: "1px solid #eef2f7", position: "sticky", top: 0, zIndex: 10 }}>
            <div style={{ padding: 12, fontWeight: 900, color: "#111827", position: "sticky", left: 0, zIndex: 12, background: "#fbfcfe", borderRight: "1px solid #eef2f7" }}>Table</div>
            {timeSlots.map(slot => (
              <div key={slot} style={{ padding: 12, fontSize: 12, fontWeight: 900, color: "#111827", borderLeft: "1px solid #eef2f7", textAlign: "center", whiteSpace: "nowrap" }}>{slot}</div>
            ))}
          </div>

          {/* Body - Grouped by location */}
          {Object.entries(groupedTables).map(([loc, locTables]) => (
            <React.Fragment key={loc}>
              <div style={{ background: '#f8fafc', padding: 8, position: "sticky", left: 0, zIndex: 9, fontWeight: 700, color: '#6b7280' }}>{loc}</div>
              {locTables.map(t => {
                const row = { key: t.id, title: `${t.label} (${t.capacity})`, location: t.location };
                if (!filteredRows.some(r => r.key === row.key)) return null;
                return (
                  <div key={row.key} className="rcg-row" style={{ display: "grid", gridTemplateColumns: `240px repeat(${timeSlots.length}, 92px)`, borderBottom: "1px solid #eef2f7", minHeight: 54 }}>
                    <div style={{ padding: 12, background: "#fff", position: "sticky", left: 0, zIndex: 8, borderRight: "1px solid #eef2f7", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
                      <div style={{ fontWeight: 900, color: "#111827" }}>{row.title}</div>
                      {row.location ? <div style={{ fontSize: 12, color: "#6b7280" }}>{row.location}</div> : null}
                    </div>
                    {timeSlots.map(slot => {
                      const cell = getCell(row.key, slot);
                      if (cell.type === "skip") return <div key={`${row.key}-${slot}`} style={{ borderLeft: "1px solid #eef2f7", background: "#fff" }} />;
                      if (cell.type === "res") {
                        const r = cell.res;
                        const theme = statusTheme?.[r.status] || {};
                        const span = Math.min(cell.span, timeSlots.length);
                        return (
                          <div
                            key={`${row.key}-${slot}`}
                            draggable
                            onDragStart={() => handleDragStart(r)}
                            style={{
                              gridColumn: `span ${span}`,
                              padding: 6,
                              borderLeft: "1px solid #eef2f7",
                              cursor: "move",
                              background: "#fff",
                            }}
                            onClick={() => onSelect?.(r)}
                          >
                            <Tooltip title={`${r.customerName} • ${moment(r.start).format("HH:mm")}–${moment(r.end).format("HH:mm")}`}>
                              <div
                                style={{
                                  height: 42,
                                  borderRadius: 12,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 900,
                                  background: (theme.bg || "#1677ff") + "22",
                                  border: `1px solid ${theme.border || theme.bg || "#1677ff"}`,
                                  color: theme.text || "#111827",
                                  textDecoration: theme.strike ? "line-through" : "none",
                                  borderStyle: theme.dashed ? "dashed" : "solid",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  padding: "0 10px",
                                }}
                              >
                                {r.customerName} <UsergroupAddOutlined style={{ marginLeft: 4 }} /> {r.partySize}
                              </div>
                            </Tooltip>
                          </div>
                        );
                      }
                      // empty cell
                      return (
                        <Tooltip title="Add New Reservation" key={`${row.key}-${slot}`}>
                          <div
                            className="rcg-cell"
                            style={{
                              borderLeft: "1px solid #eef2f7",
                              cursor: "pointer",
                              background: "#fff",
                              position: "relative",
                            }}
                            onClick={() => {
                              const slotStart = moment.tz(`${ymd} ${slot}`, "YYYY-MM-DD HH:mm", tz);
                              onCreate?.({
                                date: ymd,
                                time: slot,
                                start: slotStart.toISOString(),
                                end: slotStart.clone().add(slotMin, "minutes").toISOString(),
                                tableId: row.key !== "unassigned" ? row.key : "unassigned",
                              });
                            }}
                            onDrop={() => handleDrop(row.key, slot)}
                            onDragOver={e => e.preventDefault()}
                          >
                            <div
                              className="rcg-plus"
                              style={{
                                height: 54,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#cbd5e1",
                                fontSize: 24,
                                fontWeight: 900,
                                opacity: 0.5,
                                transition: "all 0.2s ease",
                                userSelect: "none",
                              }}
                            >
                              +
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {showUnassignedRow && filteredRows.some(r => r.key === 'unassigned') && (
            <div className="rcg-row" style={{ display: "grid", gridTemplateColumns: `240px repeat(${timeSlots.length}, 92px)`, borderBottom: "1px solid #eef2f7", minHeight: 54 }}>
              <div style={{ padding: 12, background: "#fff", position: "sticky", left: 0, zIndex: 8, borderRight: "1px solid #eef2f7", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
                <div style={{ fontWeight: 900, color: "#111827" }}>Unassigned</div>
              </div>
              {timeSlots.map(slot => {
                const cell = getCell('unassigned', slot);
                if (cell.type === "skip") return <div key={`unassigned-${slot}`} style={{ borderLeft: "1px solid #eef2f7", background: "#fff" }} />;
                if (cell.type === "res") {
                  const r = cell.res;
                  const theme = statusTheme?.[r.status] || {};
                  const span = Math.min(cell.span, timeSlots.length);
                  return (
                    <div
                      key={`unassigned-${slot}`}
                      draggable
                      onDragStart={() => handleDragStart(r)}
                      style={{
                        gridColumn: `span ${span}`,
                        padding: 6,
                        borderLeft: "1px solid #eef2f7",
                        cursor: "move",
                        background: "#fff",
                      }}
                      onClick={() => onSelect?.(r)}
                    >
                      <Tooltip title={`${r.customerName} • ${moment(r.start).format("HH:mm")}–${moment(r.end).format("HH:mm")}`}>
                        <div
                          style={{
                            height: 42,
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 900,
                            background: (theme.bg || "#1677ff") + "22",
                            border: `1px solid ${theme.border || theme.bg || "#1677ff"}`,
                            color: theme.text || "#111827",
                            textDecoration: theme.strike ? "line-through" : "none",
                            borderStyle: theme.dashed ? "dashed" : "solid",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            padding: "0 10px",
                          }}
                        >
                          {r.customerName} <UsergroupAddOutlined style={{ marginLeft: 4 }} /> {r.partySize}
                        </div>
                      </Tooltip>
                    </div>
                  );
                }
                return (
                  <Tooltip title="Add New Reservation" key={`unassigned-${slot}`}>
                    <div
                      className="rcg-cell"
                      style={{
                        borderLeft: "1px solid #eef2f7",
                        cursor: "pointer",
                        background: "#fff",
                        position: "relative",
                      }}
                      onClick={() => {
                        const slotStart = moment.tz(`${ymd} ${slot}`, "YYYY-MM-DD HH:mm", tz);
                        onCreate?.({
                          date: ymd,
                          time: slot,
                          start: slotStart.toISOString(),
                          end: slotStart.clone().add(slotMin, "minutes").toISOString(),
                          tableId: "unassigned",
                        });
                      }}
                      onDrop={() => handleDrop('unassigned', slot)}
                      onDragOver={e => e.preventDefault()}
                    >
                      <div
                        className="rcg-plus"
                        style={{
                          height: 54,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#cbd5e1",
                          fontSize: 24,
                          fontWeight: 900,
                          opacity: 0.5,
                          transition: "all 0.2s ease",
                          userSelect: "none",
                        }}
                      >
                        +
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}

          {/* Current Time Indicator */}
          {currentTimePosition >= 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 240 + currentTimePosition,
                height: "100%",
                width: 2,
                background: "#ef4444",
                zIndex: 20,
                opacity: 0.9,
              }}
            />
          )}
        </div>
      </div>
      <style>{`
        .ns-rcg-root {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
          overflow: hidden;
        }

        .ns-rcg-toolbar {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 34%),
            #ffffff;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ns-rcg-toolbar-title {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .ns-rcg-toolbar-icon {
          width: 40px;
          height: 40px;
          border-radius: 15px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 12px 24px rgba(37, 99, 235, 0.18);
        }

        .ns-rcg-toolbar-title strong {
          color: #0f172a;
          display: block;
          font-size: 0.92rem;
          font-weight: 950;
        }

        .ns-rcg-toolbar-title span {
          color: #64748b;
          display: block;
          font-size: 0.72rem;
          font-weight: 780;
          margin-top: 2px;
        }

        .ns-rcg-toolbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ns-rcg-toolbar .ant-input-affix-wrapper,
        .ns-rcg-filter-select .ant-select-selector {
          min-height: 40px;
          border-radius: 14px !important;
          border-color: rgba(15, 23, 42, 0.10) !important;
          font-weight: 800;
        }

        .ns-rcg-scroll {
          -webkit-overflow-scrolling: touch;
          background: #f8fafc;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .rcg-head {
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
        }

        .rcg-head > div {
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .rcg-row:hover {
          background: #f9fafb;
        }

        .rcg-cell:hover {
          background: #eff6ff !important;
        }

        .rcg-cell:hover .rcg-plus {
          opacity: 1 !important;
          color: #1d4ed8 !important;
          font-size: 30px !important;
          transform: scale(1.08);
        }

        .rcg-plus {
          border-radius: 999px;
        }

        @media (max-width: 768px) {
          .ns-rcg-toolbar,
          .ns-rcg-toolbar-actions {
            align-items: stretch;
          }

          .ns-rcg-toolbar-actions,
          .ns-rcg-toolbar .ant-input-affix-wrapper,
          .ns-rcg-filter-select {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}